import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "motion/react";
import Patio from "images/OutsidePatioCompressed.jpg";
import SeatingLanding from "images/SeatingLandingCompressed.jpg";
import FoodLanding from "images/FoodLandingCompressed.jpg";
import InActionFood from "images/InActionFoodCompressed.jpg";
import ReviewDisplayer from "components/ReviewDisplayer";
import { useNavigate } from "react-router-dom";
import { welcomeSvg } from "svg";
import Eater from "images/eater.jpg";
import HolidayPopup from "components/HolidayPopup";

const riseIn = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.8, ease: "easeOut" },
};

/* Gentler variant for the full-width bands (Eater, reviews, badges) */
const riseInSoft = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.8, ease: "easeOut" },
};

/* Image clipped by its frame and slightly oversized, drifting
   against the scroll so it feels suspended in the page. The frame
   starts tilted (like the menu page photos) and straightens out
   as it scrolls toward the middle of the viewport. */
function ParallaxImage({ className, src, alt, drift = "6%", position, tilt = 0 }) {
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    reducedMotion ? ["0%", "0%"] : [`-${drift}`, drift]
  );
  const rotate = useTransform(
    scrollYProgress,
    [0, 0.45],
    reducedMotion ? [0, 0] : [tilt, 0]
  );

  return (
    <motion.div
      ref={ref}
      className={className}
      style={tilt ? { rotate, willChange: "transform" } : undefined}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y, willChange: "transform", objectPosition: position }}
      />
    </motion.div>
  );
}

function EditorialFigure({
  side,
  title,
  src,
  alt,
  position,
  glow,
  glow2,
  children,
}) {
  return (
    <motion.section
      className={`editorial-figure ef-${side}`}
      style={{ "--ef-glow": glow, "--ef-glow2": glow2 || glow }}
      {...riseIn}
    >
      <div className="ef-text">
        <h3 className="ef-title">{title}</h3>
        {children}
      </div>
      <ParallaxImage
        className="ef-media"
        src={src}
        alt={alt}
        position={position}
        tilt={side === "right" ? 2.2 : -2.2}
      />
    </motion.section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  const { scrollY } = useScroll();
  const color = useTransform(scrollY, [100, 500], ["#ffffff", "#d3963a"]);

  /* Hero lags behind the scroll as it leaves the viewport */
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(
    heroProgress,
    [0, 1],
    reducedMotion ? ["0%", "0%"] : ["0%", "10%"]
  );

  /* Eater logo drifts gently against the scroll */
  const featuredRef = useRef(null);
  const { scrollYProgress: featuredProgress } = useScroll({
    target: featuredRef,
    offset: ["start end", "end start"],
  });
  const eaterY = useTransform(
    featuredProgress,
    [0, 1],
    reducedMotion ? [0, 0] : [6, -6]
  );


  const date = new Date();
  const day = date.getDate();
  const month = date.getMonth();
  const isHolidays = month === 11 || (month === 0 && day === 1);

  return (
    <>
      {isHolidays && <HolidayPopup />}
      <div className="home">
        <div className="home-header">
          <div className="home-pic" ref={heroRef}>
            <motion.img
              style={{
                position: "absolute",
                top: "-12%",
                left: 0,
                width: "100%",
                height: "112%",
                objectFit: "cover",
                objectPosition: "center",
                y: heroY,
                willChange: "transform",
              }}
              className="home-pic-img"
              src={Patio}
              alt="Patio"
              loading="lazy"
            />
            <div className="pic-overlay">
              <div className="pic-content">{welcomeSvg()}</div>
            </div>
          </div>
        </div>

        <motion.section className="editorial-intro" {...riseIn}>
          <div className="intro-frame">
            <div className="hero-text intro-title">
              Old world Italian
              <br />
              in the{" "}
              <motion.span style={{ color: color }}> heart </motion.span> of
              Evanston
            </div>
            <h2 className="intro-since">Since 1993</h2>
            <p className="intro-line">
              Family-owned and operated for over three decades, we're that
              little Italian joint in the neighborhood.
            </p>
          </div>
        </motion.section>

        <EditorialFigure
          side="right"
          title="Come have a seat"
          src={SeatingLanding}
          alt="A window table set and waiting at Trattoria Demi"
          position="center 100%"
          glow="rgba(222, 178, 64, 0.11)"
          glow2="rgba(132, 86, 44, 0.13)"
        >
          <p className="ef-sub">
            Cozy casual Italian, like your Nonna used to make it — step in
            and let our family serve yours.
          </p>
        </EditorialFigure>

        <section className="menu-band">
          <ParallaxImage
            className="menu-band-media"
            src={FoodLanding}
            alt="An overhead spread of Trattoria Demi dishes and wine"
            drift="8%"
          />
          <div className="menu-band-overlay">
            <motion.div className="menu-band-content" {...riseIn}>
              <h3 className="menu-band-title">
                Made fresh,
                <br className="mobile-br" /> every day
              </h3>
              <button
                className="ghost-button"
                type="button"
                onClick={() => navigate("/menu")}
              >
                View our Menu
              </button>
            </motion.div>
          </div>
        </section>

        <EditorialFigure
          side="left"
          title="Food for everyone"
          src={InActionFood}
          alt="A forkful of pasta lifted from the plate"
          glow="rgba(216, 152, 78, 0.1)"
          glow2="rgba(168, 54, 28, 0.13)"
        >
          <p className="ef-sub">
            A blend of traditional and innovative Italian, crafted from fresh,
            high-quality ingredients — with plenty of vegan and gluten-free
            options, so everyone at the table eats well.
          </p>
          <div className="ef-actions">
            <button
              className="subtle-button"
              type="button"
              onClick={() => navigate("/gallery")}
            >
              View our gallery
            </button>
          </div>
        </EditorialFigure>

        <div className="featured-block" ref={featuredRef}>
          <motion.div className="featured-top" {...riseInSoft}>
            <div className="featured-text">
              <div className="hero-text"> Featured in </div>
              <h2 className="article-text">
                <a
                  href="https://chicago.eater.com/maps/the-best-pasta-in-chicago"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  The Best Pasta in Chicago
                </a>{" "}
              </h2>
            </div>
            <motion.a
              href="https://chicago.eater.com/maps/the-best-pasta-in-chicago"
              target="_blank"
              rel="noopener noreferrer"
              className="eater-wrapper"
              style={{ y: eaterY }}
            >
              <img src={Eater} alt="Eater" className="eater-img" />
            </motion.a>
          </motion.div>
        </div>

        <motion.div
          className="review-container"
          {...riseInSoft}
          viewport={{ once: true, amount: 0.15 }}
        >
          <ReviewDisplayer />
        </motion.div>
      </div>
    </>
  );
}
